/**
 * Hook personalizado para manejar fechas especiales, festivos y horarios bloqueados
 * Integra todos los servicios de fechas en una sola interfaz fácil de usar
 */

import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import * as festivosService from '@/services/festivosApiService';
import * as fechasEspecialesService from '@/services/fechasEspecialesApiService';
import * as horariosBloqueadosService from '@/services/horariosBloqueadosApiService';
import { useSucursal } from '@/context/SucursalContext';

/**
 * Hook para manejar fechas especiales, festivos y horarios bloqueados
 */
export const useFechasEspeciales = () => {
  const { selectedSucursal } = useSucursal();
  
  // Helper para obtener el ID de sucursal (puede ser número directo o objeto con .id)
  const getSucursalId = () => {
    if (typeof selectedSucursal === 'number') return selectedSucursal;
    if (typeof selectedSucursal === 'string' && selectedSucursal !== '') return parseInt(selectedSucursal);
    if (selectedSucursal?.id) return selectedSucursal.id;
    return null;
  };
  
  const sucursalId = getSucursalId();
  
  
  // Estados para cache de datos
  const [festivos, setFestivos] = useState([]);
  const [fechasEspeciales, setFechasEspeciales] = useState([]);
  const [horariosBloqueados, setHorariosBloqueados] = useState([]);
  
  // Estados de carga
  const [loadingFestivos, setLoadingFestivos] = useState(false);
  const [loadingFechasEspeciales, setLoadingFechasEspeciales] = useState(false);
  const [loadingHorariosBloqueados, setLoadingHorariosBloqueados] = useState(false);
  
  // Cache por fecha para optimizar consultas
  const [cacheConsultas, setCacheConsultas] = useState({});

  /**
   * Carga festivos para un año específico y sucursal
   */
  const cargarFestivos = useCallback(async (year = moment().year()) => {
    if (!sucursalId) {
      return;
    }
    
    try {
      setLoadingFestivos(true);
      
      // Obtener configuración de festivos específica para la sucursal
      const configuracionFestivos = await festivosService.getConfiguracionFestivosSucursal(sucursalId);
      setFestivos(configuracionFestivos.festivos || []);
    } catch (error) {
      console.error('❌ [useFechasEspeciales] Error al cargar festivos:', error);
      setFestivos([]);
    } finally {
      setLoadingFestivos(false);
    }
  }, [sucursalId]);

  /**
   * Carga fechas especiales para la sucursal actual
   */
  const cargarFechasEspeciales = useCallback(async () => {
    if (!sucursalId) {
      return;
    }
    
    try {
      setLoadingFechasEspeciales(true);
      
      const fechasData = await fechasEspecialesService.getFechasEspecialesBySucursal(sucursalId);
      setFechasEspeciales(fechasData);
    } catch (error) {
      console.error('❌ [useFechasEspeciales] Error al cargar fechas especiales:', error);
      setFechasEspeciales([]);
    } finally {
      setLoadingFechasEspeciales(false);
    }
  }, [sucursalId]);

  /**
   * Carga horarios bloqueados para la sucursal actual
   */
  const cargarHorariosBloqueados = useCallback(async () => {
    if (!sucursalId) {
      return;
    }
    
    try {
      setLoadingHorariosBloqueados(true);
      
      const horariosData = await horariosBloqueadosService.getHorariosBloqueadosBySucursal(sucursalId);
      setHorariosBloqueados(horariosData);
    } catch (error) {
      console.error('❌ [useFechasEspeciales] Error al cargar horarios bloqueados:', error);
      setHorariosBloqueados([]);
    } finally {
      setLoadingHorariosBloqueados(false);
    }
  }, [sucursalId]);

  /**
   * Verifica si una fecha es festiva
   */
  const esFechaFestiva = useCallback((fecha) => {
    return festivos.some(festivo => {
      // Verificar que el festivo esté activo
      if (festivo.activo === false) return false;
      
      // Obtener la fecha del festivo desde la relación con festivos_nacionales
      let fechaFestivo;
      let recurrente = false;
      
      if (festivo.festivos_nacionales) {
        fechaFestivo = moment(festivo.festivos_nacionales.fecha);
        // Si recurrente no viene definido, asumir true para festivos nacionales
        recurrente = festivo.festivos_nacionales.recurrente !== undefined ? 
          festivo.festivos_nacionales.recurrente : true;
      } else if (festivo.fecha) {
        fechaFestivo = moment(festivo.fecha);
        recurrente = festivo.recurrente || false;
      } else {
        return false;
      }
      
      // Si es recurrente, comparar solo mes y día
      if (recurrente) {
        return moment(fecha).month() === fechaFestivo.month() && 
               moment(fecha).date() === fechaFestivo.date();
      }
      
      // Si no es recurrente, comparar fecha exacta
      return moment(fecha).isSame(fechaFestivo, 'day');
    });
  }, [festivos]);

  /**
   * Verifica si una fecha es especial
   */
  const esFechaEspecial = useCallback((fecha) => {
    const fechaStr = moment(fecha).format('YYYY-MM-DD');
    return fechasEspeciales.some(fechaEspecial => {
      const fechaInicio = moment(fechaEspecial.fecha_inicio);
      const fechaFin = fechaEspecial.fecha_fin ? moment(fechaEspecial.fecha_fin) : fechaInicio;
      
      return moment(fecha).isBetween(fechaInicio, fechaFin, 'day', '[]');
    });
  }, [fechasEspeciales]);

  /**
   * Obtiene información completa de una fecha (festiva, especial, horarios)
   */
  const getInfoFecha = useCallback(async (fecha) => {
    const fechaStr = moment(fecha).format('YYYY-MM-DD');
    
    // Verificar cache primero usando referencia al estado actual
    const currentCache = cacheConsultas;
    if (currentCache[fechaStr]) {
      return currentCache[fechaStr];
    }

    const info = {
      fecha: fechaStr,
      esFestiva: esFechaFestiva(fecha),
      esEspecial: esFechaEspecial(fecha),
      festivo: null,
      fechaEspecial: null,
      horariosBloqueados: [],
      horariosDisponibles: null
    };

    // Obtener información de festivo
    if (info.esFestiva) {
      info.festivo = festivos.find(festivo => {
        // Verificar que el festivo esté activo
        if (festivo.activo === false) return false;
        
        // Obtener la fecha del festivo desde la relación con festivos_nacionales
        let fechaFestivo;
        let recurrente = false;
        
        if (festivo.festivos_nacionales) {
          fechaFestivo = moment(festivo.festivos_nacionales.fecha);
          // Si recurrente no viene definido, asumir true para festivos nacionales
          recurrente = festivo.festivos_nacionales.recurrente !== undefined ? 
            festivo.festivos_nacionales.recurrente : true;
        } else if (festivo.fecha) {
          fechaFestivo = moment(festivo.fecha);
          recurrente = festivo.recurrente || false;
        } else {
          return false;
        }
        
        // Si es recurrente, comparar solo mes y día
        if (recurrente) {
          return fecha.month() === fechaFestivo.month() && 
                 fecha.date() === fechaFestivo.date();
        }
        
        // Si no es recurrente, comparar fecha exacta
        return fecha.isSame(fechaFestivo, 'day');
      });
    }

    // Obtener información de fecha especial
    if (info.esEspecial) {
      info.fechaEspecial = fechasEspeciales.find(fechaEspecial => {
        const fechaInicio = moment(fechaEspecial.fecha_inicio);
        const fechaFin = fechaEspecial.fecha_fin ? moment(fechaEspecial.fecha_fin) : fechaInicio;
        
        return fecha.isBetween(fechaInicio, fechaFin, 'day', '[]');
      });
    }

    // Obtener horarios bloqueados para la fecha
    const diaNormalizado = fecha.locale('es').format('dddd').toLowerCase()
      .replace('á', 'a').replace('é', 'e').replace('í', 'i').replace('ó', 'o').replace('ú', 'u');
    
    info.horariosBloqueados = horariosBloqueados.filter(horario => {
      if (!horario.activo) return false;
      
      // Verificar si el bloqueo aplica a este día
      if (horario.dia_semana !== 'todos' && horario.dia_semana !== diaNormalizado) {
        return false;
      }
      
      // Verificar vigencia del bloqueo
      const fechaInicio = moment(horario.fecha_inicio_vigencia);
      const fechaFin = horario.fecha_fin_vigencia ? moment(horario.fecha_fin_vigencia) : null;
      
      if (fecha.isBefore(fechaInicio, 'day')) return false;
      if (fechaFin && fecha.isAfter(fechaFin, 'day')) return false;
      
      return true;
    });

    // Cache del resultado
    setCacheConsultas(prev => ({
      ...prev,
      [fechaStr]: info
    }));

    return info;
  }, [esFechaFestiva, esFechaEspecial, festivos, fechasEspeciales, horariosBloqueados]);

  /**
   * Verifica si un horario específico está bloqueado
   */
  const esHorarioBloqueado = useCallback((fecha, hora) => {
    const horaStr = moment(hora).format('HH:mm');
    const diaNormalizado = fecha.locale('es').format('dddd').toLowerCase()
      .replace('á', 'a').replace('é', 'e').replace('í', 'i').replace('ó', 'o').replace('ú', 'u');
    
    const estaBloqueado = horariosBloqueados.some(horario => {
      
      if (!horario.activo) return false;
      
      // Verificar si el bloqueo aplica a este día
      if (horario.dia_semana !== 'todos' && horario.dia_semana !== diaNormalizado) {
        return false;
      }
      
      // Verificar vigencia del bloqueo
      const fechaInicio = moment(horario.fecha_inicio_vigencia);
      const fechaFin = horario.fecha_fin_vigencia ? moment(horario.fecha_fin_vigencia) : null;
      
      if (fecha.isBefore(fechaInicio, 'day')) return false;
      if (fechaFin && fecha.isAfter(fechaFin, 'day')) return false;
      
      // Verificar si la hora está en el rango bloqueado
      // Normalizar formatos de hora para comparación correcta
      const horaSlotNormalizada = horaStr.length === 5 ? horaStr + ':00' : horaStr;
      const horaInicioNormalizada = horario.hora_inicio.length === 5 ? horario.hora_inicio + ':00' : horario.hora_inicio;
      const horaFinNormalizada = horario.hora_fin.length === 5 ? horario.hora_fin + ':00' : horario.hora_fin;
      
      const estaEnRango = horaSlotNormalizada >= horaInicioNormalizada && horaSlotNormalizada < horaFinNormalizada;
      return estaEnRango;
    });
    
    return estaBloqueado;
  }, [horariosBloqueados]);

  /**
   * Obtiene horarios disponibles para una fecha, excluyendo bloqueados
   */
  const getHorariosDisponibles = useCallback((fecha, horariosBase) => {
    if (!horariosBase || horariosBase.length === 0) {
      return [];
    }

    const horariosDisponibles = horariosBase.filter(hora => {
      const estaBloqueado = esHorarioBloqueado(fecha, hora);
      return !estaBloqueado;
    });

    return horariosDisponibles;
  }, [esHorarioBloqueado]);

  /**
   * Verifica si una fecha es laborable (no festiva, no especial con cierre)
   */
  const esFechaLaborable = useCallback(async (fecha) => {
    const info = await getInfoFecha(fecha);
    
    // Si es festiva, no es laborable
    if (info.esFestiva) {
      return false;
    }
    
    // Si es fecha especial y está marcada como cerrada, no es laborable
    if (info.esEspecial && info.fechaEspecial) {
      return info.fechaEspecial.tipo !== 'cerrado';
    }
    
    return true;
  }, [getInfoFecha]);

  /**
   * Obtiene el próximo día laborable después de una fecha
   */
  const getProximoDiaLaborable = useCallback(async (fecha) => {
    let fechaActual = moment(fecha).add(1, 'day');
    let intentos = 0;
    const maxIntentos = 30; // Evitar loops infinitos
    
    while (intentos < maxIntentos) {
      const esLaborable = await esFechaLaborable(fechaActual);
      
      if (esLaborable) {
        return fechaActual.toDate();
      }
      
      fechaActual.add(1, 'day');
      intentos++;
    }
    
    // Si no encuentra día laborable en 30 días, retorna el día siguiente original
    return moment(fecha).add(1, 'day').toDate();
  }, [esFechaLaborable]);

  /**
   * Crea un nuevo horario bloqueado
   */
  const crearHorarioBloqueado = useCallback(async (horarioData) => {
    try {
      const nuevoHorario = await horariosBloqueadosService.createHorarioBloqueado({
        ...horarioData,
        sucursal_id: sucursalId
      });
      
      // Actualizar cache local
      setHorariosBloqueados(prev => [...prev, nuevoHorario]);
      
      // Limpiar cache de consultas
      setCacheConsultas({});
      
      return nuevoHorario;
    } catch (error) {
      console.error('Error al crear horario bloqueado:', error);
      throw error;
    }
  }, [sucursalId]);

  /**
   * Elimina un horario bloqueado
   */
  const eliminarHorarioBloqueado = useCallback(async (horarioId) => {
    try {
      await horariosBloqueadosService.deleteHorarioBloqueado(horarioId);
      
      // Actualizar cache local
      setHorariosBloqueados(prev => prev.filter(h => h.id !== horarioId));
      
      // Limpiar cache de consultas
      setCacheConsultas({});
      
      return true;
    } catch (error) {
      console.error('Error al eliminar horario bloqueado:', error);
      throw error;
    }
  }, []);

  /**
   * Refresca todos los datos
   */
  const refrescarDatos = useCallback(async () => {
    setCacheConsultas({});
    
    const year = moment().year();
    await Promise.all([
      cargarFestivos(year),
      cargarFechasEspeciales(),
      cargarHorariosBloqueados()
    ]);
  }, [cargarFestivos, cargarFechasEspeciales, cargarHorariosBloqueados]);

  // Cargar datos iniciales cuando cambia la sucursal
  useEffect(() => {
    if (sucursalId) {
      const year = moment().year();
      cargarFestivos(year);
      cargarFechasEspeciales();
      cargarHorariosBloqueados();
      setCacheConsultas({});
    }
  }, [sucursalId, cargarFestivos, cargarFechasEspeciales, cargarHorariosBloqueados]);

  return {
    // Estados
    festivos,
    fechasEspeciales,
    horariosBloqueados,
    
    // Estados de carga
    loadingFestivos,
    loadingFechasEspeciales,
    loadingHorariosBloqueados,
    loading: loadingFestivos || loadingFechasEspeciales || loadingHorariosBloqueados,
    
    // Funciones de verificación
    esFechaFestiva,
    esFechaEspecial,
    esFechaLaborable,
    esHorarioBloqueado,
    
    // Funciones de información
    getInfoFecha,
    getHorariosDisponibles,
    getProximoDiaLaborable,
    
    // Funciones CRUD
    crearHorarioBloqueado,
    eliminarHorarioBloqueado,
    
    // Funciones de carga
    cargarFestivos,
    cargarFechasEspeciales,
    cargarHorariosBloqueados,
    refrescarDatos
  };
};